import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, LogIn, Users, Loader2 } from 'lucide-react';

export default function CoachDashboardPage() {
  const { user, setUser } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('arch_token');
      if (!token) {
        toast.error('Not authenticated');
        return;
      }
      
      const res = await axios.get(`${API}/coach/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(res.data.assignments || []);
    } catch (err) {
      console.error('Assignment load error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to load assigned organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (orgId, orgName) => {
    setImpersonating(true);
    try {
      const token = localStorage.getItem('arch_token');
      if (!token) {
        toast.error('Not authenticated - please login again');
        return;
      }
      
      console.log('Attempting to impersonate:', orgId, orgName);
      
      const res = await axios.post(`${API}/coach/impersonate/${orgId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Impersonation response:', res.data);
      
      if (res.data.access_token) {
        localStorage.setItem('arch_token', res.data.access_token);
        setUser(res.data.user);
        toast.success(`Signed in as ${orgName}`);
        
        // Force page reload to dashboard
        setTimeout(() => {
          window.location.href = '/';
        }, 500);
      } else {
        toast.error('No access token received');
      }
    } catch (err) {
      console.error('Impersonation error:', err);
      console.error('Error response:', err?.response);
      toast.error(err?.response?.data?.detail || 'Failed to sign in as organization');
    } finally {
      setImpersonating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#0A0F1E' }}>
        <Loader2 className="animate-spin text-blue-400" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ background: '#0A0F1E' }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)' }}>
              <Users size={24} className="text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white font-heading">Coach Dashboard</h1>
              <p className="text-slate-400 text-sm">Welcome, {user?.full_name}</p>
            </div>
          </div>
        </div>

        {/* Assigned Organizations */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Your Assigned Organizations</h2>
          {assignments.length === 0 ? (
            <Card className="p-12 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <Building2 size={48} className="mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400 text-sm">No organizations assigned yet</p>
              <p className="text-slate-500 text-xs mt-1">Contact your administrator to get started</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {assignments.map(org => (
                <Card key={org.org_id} className="p-6 hover:scale-[1.01] transition-transform"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.25)' }}>
                        <Building2 size={20} className="text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{org.org_name}</h3>
                        <p className="text-sm text-slate-400">
                          {org.location_count} location{org.location_count !== 1 ? 's' : ''}
                          {' • '}
                          {org.user_count} user{org.user_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleImpersonate(org.org_id, org.org_name)}
                      disabled={impersonating}
                      className="gap-2"
                      data-testid={`sign-in-as-${org.org_id}`}
                    >
                      {impersonating ? (
                        <><Loader2 size={14} className="animate-spin" /> Signing in...</>
                      ) : (
                        <><LogIn size={14} /> Sign In As</>
                      )}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
